import type { ReactNode } from 'react';
import { Button, IconButton } from './Button';
import { AlertCircle, AlertTriangle, CheckCircle2, Info, X } from './icons';

export interface BannerMessage {
  id: string;
  tone?: 'info' | 'warning' | 'danger' | 'success';
  body: ReactNode;
  action?: { label: string; onClick: () => void };
  onDismiss?: () => void;
}

const toneIcon = { info: Info, warning: AlertTriangle, danger: AlertCircle, success: CheckCircle2 } as const;

/** The shell's notification region (P38): urgency-coloured rows with an icon, one action and a dismiss each. */
export function Banners({ messages }: { messages: BannerMessage[] }) {
  if (messages.length === 0) return null;
  return (
    <div className="banners" role="region" aria-label="Notifications">
      {messages.map((m) => {
        const Icon = toneIcon[m.tone ?? 'info'];
        return (
          <div key={m.id} className={`banner ${m.tone && m.tone !== 'info' ? `banner-${m.tone}` : ''}`} role={m.tone === 'danger' ? 'alert' : 'status'}>
            <Icon className="banner-icon" aria-hidden="true" />
            <div className="banner-body">{m.body}</div>
            {m.action && (
              <Button size="s" onClick={m.action.onClick}>
                {m.action.label}
              </Button>
            )}
            {m.onDismiss && (
              <IconButton label="Dismiss" onClick={m.onDismiss}>
                <X aria-hidden="true" />
              </IconButton>
            )}
          </div>
        );
      })}
    </div>
  );
}

/** An empty state that teaches the page: an icon, a title, one sentence, the action (spec section 2.2). */
export function EmptyState({ title, body, action, icon, role = 'note', className = '' }: { title: string; body?: ReactNode; action?: ReactNode; icon?: ReactNode; /** `note` by default; `null` for no role (a page that already has a note), `status` for a state that changes */ role?: 'note' | 'status' | null; className?: string }) {
  return (
    <div className={`empty ${className}`} role={role ?? undefined}>
      {icon && (
        <span className="empty-icon" aria-hidden="true">
          {icon}
        </span>
      )}
      <h3>{title}</h3>
      {body && <p>{body}</p>}
      {action}
    </div>
  );
}
